import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Play, Trash2 } from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { EmptyState, Panel, StatusBadge } from "@/components/common/Primitives";
import { api, apiError } from "@/lib/api";
import { dateTime, num } from "@/lib/format";

export default function ScanHistory() {
  const navigate = useNavigate();
  const [scans, setScans] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/scans");
      setScans(data);
    } catch (e) {
      console.error("Scanhistorie laden mislukt:", e);
      setError(apiError(e));
    }
    setLoading(false);
    // api/apiError are stable module singletons and state setters are stable,
    // so this callback intentionally has no dependencies.
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <AppShell title="Scanhistorie" subtitle="Elke scan wordt opgeslagen met filters, bronnen en resultaat">
      {error && (
        <div className="mb-3 rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-800">
          {error}
        </div>
      )}
      <Panel testid="scan-history-panel">
        {loading ? (
          <div className="px-4 py-10 text-center text-[13px] text-slate-500">Laden…</div>
        ) : scans.length === 0 ? (
          <EmptyState
            title="Nog geen scans uitgevoerd"
            description="Start een scan via 'Nieuwe Scan'. Elke scan wordt hier bewaard en kan exact opnieuw worden uitgevoerd."
            testid="scan-history-empty"
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Datum / tijd</th>
                  <th>Profiel</th>
                  <th>Filters</th>
                  <th>Databronnen</th>
                  <th>Status</th>
                  <th>Voertuigen</th>
                  <th>Kansen boven drempel</th>
                  <th>Top score</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {scans.map((scan) => (
                  <tr key={scan.id} data-testid={`scan-row-${scan.id}`}>
                    <td className="num">{dateTime(scan.created_at)}</td>
                    <td className="text-[12px] font-semibold">{scan.profile_name || "—"}</td>
                    <td className="max-w-[280px] truncate text-[11.5px] text-slate-600">
                      {[
                        scan.filters?.make,
                        scan.filters?.model,
                        scan.filters?.generation,
                        scan.filters?.year_from && `vanaf ${scan.filters.year_from}`,
                        scan.filters?.mileage_max && `max ${num(scan.filters.mileage_max)} km`,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </td>
                    <td className="text-[11.5px]">{(scan.sources || []).join(", ")}</td>
                    <td>
                      {scan.status === "COMPLETED" ? (
                        <StatusBadge status="CONNECTED" label="Voltooid" />
                      ) : scan.status === "ERROR" ? (
                        <StatusBadge status="ERROR" label="Fout" />
                      ) : (
                        <span className="chip border-blue-300 bg-blue-50 text-blue-800">
                          {scan.stage_message || scan.status}
                        </span>
                      )}
                    </td>
                    <td className="num">{num(scan.vehicles_found ?? 0)}</td>
                    <td className="num">{num(scan.opportunities ?? 0)}</td>
                    <td className="num">{scan.top_score ?? "—"}</td>
                    <td>
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          data-testid={`scan-results-${scan.id}`}
                          onClick={() => navigate(`/handelskansen?scan=${scan.id}`)}
                          className="rounded-sm border border-slate-300 px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-100"
                        >
                          Resultaten
                        </button>
                        <button
                          type="button"
                          data-testid={`scan-rerun-${scan.id}`}
                          onClick={async () => {
                            const { data } = await api.post(`/scans/${scan.id}/rerun`);
                            navigate(`/handelskansen?scan=${data.id}`);
                          }}
                          className="flex items-center gap-1 rounded-sm bg-slate-900 px-2 py-1 text-[11px] font-semibold text-white hover:bg-slate-700"
                        >
                          <Play className="h-3 w-3" /> Scan opnieuw
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            await api.delete(`/scans/${scan.id}`);
                            load();
                          }}
                          className="rounded-sm border border-slate-300 p-1 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </AppShell>
  );
}
